import { useState, useEffect } from 'react';
import { formatarNome, nomeDoPapel } from '../utils/nomes';
import { PageHeader } from '../components/common/PageHeader';
import { useFeedback } from '../components/common/Feedback';
import { useSearchParams } from 'react-router-dom';
import { AppLayout } from '../components/Layout/AppLayout';
import { apiClient } from '../api/client';
import { workspacesApi } from '../api/workspaces';
import { useInvitations } from '../hooks/useInvitations';
import {
  Building2, Users, Crown, ShieldCheck, Clock, AlertCircle,
  Plus, Trash2, Check, X, RefreshCw, ExternalLink
} from 'lucide-react';

interface OrgDetails {
  id: string;
  name: string;
  slug: string;
  plan: string;
  active: boolean;
  billing_exempt: boolean;
  is_active_workspace: boolean;
  user_role: string;
  member_count: number;
  subscription_status: 'active' | 'trial' | 'inactive' | 'exempt';
  created_at: string;
}

const statusConfig = {
  active: { label: 'Premium', color: 'var(--color-primary-400)', bg: 'rgba(26, 154, 102, 0.12)', icon: Crown },
  trial: { label: 'Trial', color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.12)', icon: Clock },
  exempt: { label: 'Isento', color: '#60a5fa', bg: 'rgba(96, 165, 250, 0.12)', icon: ShieldCheck },
  inactive: { label: 'Inativo', color: 'var(--color-text-muted)', bg: 'rgba(255,255,255,0.04)', icon: AlertCircle },
};

export function Organizations() {
  const { confirmar, avisar } = useFeedback();
  const [orgs, setOrgs] = useState<OrgDetails[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const { invitations, fetchInvitations, acceptInvitation, rejectInvitation } = useInvitations();
  const [processingInvite, setProcessingInvite] = useState<string | null>(null);

  const fetchOrgs = async () => {
    setIsLoading(true);
    try {
      const data = await apiClient.get<OrgDetails[]>('/orgs/all');
      setOrgs(data || []);
    } catch {
      setOrgs([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchOrgs();
    fetchInvitations();
  }, [fetchInvitations]);

  // Auto-aceitar convite via URL (?accept=token)
  useEffect(() => {
    const acceptToken = searchParams.get('accept');
    if (acceptToken && invitations.length > 0) {
      const inv = invitations.find(i => i.id === acceptToken);
      if (inv) {
        handleAccept(inv.id);
        setSearchParams({});
      }
    }
  }, [searchParams, invitations]);

  const handleSwitchWorkspace = async (orgId: string) => {
    try {
      const data = await workspacesApi.switch(orgId);
      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('refresh_token', data.refresh_token);
      window.location.reload();
    } catch {
      avisar('Falha ao alternar organização.', 'erro');
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newOrgName.trim()) return;
    setIsCreating(true);
    try {
      await workspacesApi.create(newOrgName.trim());
      setNewOrgName('');
      setShowCreateForm(false);
      await fetchOrgs();
    } catch (err: any) {
      avisar(err.message || 'Erro ao criar organização.', 'erro');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeactivate = async (org: OrgDetails) => {
    if (!(await confirmar({ titulo: `Desativar a organização "${org.name}"?`, mensagem: 'Isso bloqueia o acesso de todos os membros.', confirmar: 'Desativar', perigo: true }))) return;
    setDeletingId(org.id);
    try {
      await apiClient.delete(`/orgs/${org.id}`);
      if (org.is_active_workspace) {
        window.location.reload();
      } else {
        await fetchOrgs();
      }
    } catch (err: any) {
      avisar(err.message || 'Erro ao desativar organização.', 'erro');
    } finally {
      setDeletingId(null);
    }
  };

  const handleAccept = async (id: string) => {
    setProcessingInvite(id);
    try {
      await acceptInvitation(id);
      await fetchOrgs();
      avisar('Convite aceito! Você agora pertence à organização. Alterne pelo menu de organizações.', 'sucesso');
    } catch (err: any) {
      avisar(err.message || 'Erro ao aceitar convite.', 'erro');
    } finally {
      setProcessingInvite(null);
    }
  };

  const handleReject = async (id: string) => {
    setProcessingInvite(id);
    try {
      await rejectInvitation(id);
    } catch {
      /* ignora */
    } finally {
      setProcessingInvite(null);
    }
  };

  const cardStyle: React.CSSProperties = {
    background: 'var(--color-bg-surface)',
    border: '1px solid var(--color-border-default)',
    borderRadius: 'var(--radius-lg)',
    padding: 'var(--space-6)',
    transition: 'border-color 0.2s, transform 0.2s',
  };

  return (
    <AppLayout>
      <PageHeader
        title="Organizações"
        description="Gerencie todas as organizações de que você participa."
        actions={
          <>
            <button type="button" className="btn btn--outline btn--sm" onClick={fetchOrgs}>
              <RefreshCw size={16} /> Atualizar
            </button>
            <button type="button" className="btn btn--primary btn--sm" onClick={() => setShowCreateForm(!showCreateForm)}>
              <Plus size={16} /> Nova organização
            </button>
          </>
        }
      />

      {/* Convites Pendentes */}
      {invitations.length > 0 && (
        <div style={{ marginBottom: 'var(--space-8)' }}>
          <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-medium)', marginBottom: 'var(--space-4)', color: 'var(--color-text-primary)', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <span style={{ background: '#f59e0b', color: 'black', borderRadius: '50%', width: 22, height: 22, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 'bold' }}>{invitations.length}</span>
            Convites pendentes
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
            {invitations.map(inv => (
              <div key={inv.id} style={{ ...cardStyle, borderColor: 'rgba(245, 158, 11, 0.3)', background: 'rgba(245, 158, 11, 0.05)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 'var(--space-4)' }}>
                <div>
                  <div style={{ fontWeight: 'var(--font-medium)', color: 'var(--color-text-primary)' }}>{inv.org_name}</div>
                  <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
                    Convidado por <strong>{formatarNome(inv.invited_by) || inv.invited_by}</strong> · Papel: <strong>{nomeDoPapel(inv.role)}</strong>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                  <button
                    onClick={() => handleAccept(inv.id)}
                    disabled={processingInvite === inv.id}
                    style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', padding: 'var(--space-2) var(--space-4)', background: 'var(--color-primary-600)', border: 'none', borderRadius: 'var(--radius-md)', color: 'white', cursor: 'pointer', fontWeight: 'var(--font-medium)', fontSize: 'var(--text-sm)' }}
                  >
                    <Check size={14} /> Aceitar
                  </button>
                  <button
                    onClick={() => handleReject(inv.id)}
                    disabled={processingInvite === inv.id}
                    style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', padding: 'var(--space-2) var(--space-4)', background: 'transparent', border: '1px solid var(--color-error)', borderRadius: 'var(--radius-md)', color: 'var(--color-error)', cursor: 'pointer', fontSize: 'var(--text-sm)' }}
                  >
                    <X size={14} /> Recusar
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Form de nova org */}
      {showCreateForm && (
        <div style={{ ...cardStyle, marginBottom: 'var(--space-6)', borderColor: 'rgba(26, 154, 102, 0.3)' }}>
          <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-medium)', marginBottom: 'var(--space-4)' }}>Nova organização</h3>
          <form onSubmit={handleCreate} style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 250px', display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
              <label style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>Nome da organização</label>
              <input
                type="text"
                value={newOrgName}
                onChange={e => setNewOrgName(e.target.value)}
                placeholder="Ex: Estalk Advogados"
                required
                style={{ background: 'var(--color-bg-root)', border: '1px solid var(--color-border-default)', padding: 'var(--space-3)', borderRadius: 'var(--radius-md)', color: 'var(--color-text-primary)' }}
              />
            </div>
            <button type="submit" disabled={isCreating || !newOrgName.trim()} style={{ padding: 'var(--space-3) var(--space-6)', background: 'var(--color-primary-600)', border: 'none', borderRadius: 'var(--radius-md)', color: 'white', cursor: 'pointer', fontWeight: 'var(--font-medium)' }}>
              {isCreating ? 'Criando...' : 'Criar'}
            </button>
            <button type="button" onClick={() => setShowCreateForm(false)} style={{ padding: 'var(--space-3) var(--space-4)', background: 'transparent', border: '1px solid var(--color-border-default)', borderRadius: 'var(--radius-md)', color: 'var(--color-text-secondary)', cursor: 'pointer' }}>
              Cancelar
            </button>
          </form>
        </div>
      )}

      {/* Lista de organizações */}
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 'var(--space-16)', color: 'var(--color-text-secondary)' }}>Carregando...</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(340px, 100%), 1fr))', gap: 'var(--space-5)' }}>
          {orgs.map(org => {
            const sc = statusConfig[org.subscription_status] || statusConfig.inactive;
            const StatusIcon = sc.icon;
            const isOwner = org.user_role === 'owner';

            return (
              <div
                key={org.id}
                style={{
                  ...cardStyle,
                  borderColor: org.is_active_workspace ? 'rgba(26, 154, 102, 0.4)' : 'var(--color-border-default)',
                  background: org.is_active_workspace ? 'rgba(26, 154, 102, 0.04)' : 'var(--color-bg-surface)',
                  position: 'relative',
                }}
              >
                {org.is_active_workspace && (
                  <span style={{ position: 'absolute', top: 'var(--space-3)', right: 'var(--space-3)', fontSize: 'var(--text-xs)', background: 'rgba(26, 154, 102, 0.2)', color: 'var(--color-primary-400)', border: '1px solid rgba(26, 154, 102, 0.3)', padding: '2px 8px', borderRadius: 'var(--radius-full)', fontWeight: 'var(--font-medium)' }}>
                    Ativa
                  </span>
                )}

                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-3)', marginBottom: 'var(--space-4)' }}>
                  <div style={{ padding: 'var(--space-2)', background: 'var(--color-bg-elevated)', borderRadius: 'var(--radius-md)', flexShrink: 0 }}>
                    <Building2 size={20} color="var(--color-primary-400)" />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 'var(--font-semibold)', color: 'var(--color-text-primary)', fontSize: 'var(--text-base)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: org.is_active_workspace ? '60px' : '0' }}>
                      {org.name}
                    </div>
                    <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)', marginTop: '2px' }}>
                      {org.slug}
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 'var(--space-3)', marginBottom: 'var(--space-5)', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
                    <Users size={14} />
                    <span>{org.member_count} {org.member_count === 1 ? 'membro' : 'membros'}</span>
                  </div>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)', padding: '2px 10px', borderRadius: 'var(--radius-full)', background: sc.bg, color: sc.color, fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)' }}>
                    <StatusIcon size={11} />
                    {sc.label}
                  </div>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)', padding: '2px 10px', borderRadius: 'var(--radius-full)', background: 'rgba(255,255,255,0.04)', color: 'var(--color-text-secondary)', fontSize: 'var(--text-xs)' }}>
                    {nomeDoPapel(org.user_role)}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                  {!org.is_active_workspace && (
                    <button
                      onClick={() => handleSwitchWorkspace(org.id)}
                      style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-2)', padding: 'var(--space-2) var(--space-3)', background: 'var(--color-primary-600)', border: 'none', borderRadius: 'var(--radius-md)', color: 'white', cursor: 'pointer', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)' }}
                    >
                      <ExternalLink size={14} /> Acessar
                    </button>
                  )}
                  {isOwner && orgs.length > 1 && (
                    <button
                      onClick={() => handleDeactivate(org)}
                      disabled={deletingId === org.id}
                      style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', padding: 'var(--space-2) var(--space-3)', background: 'transparent', border: '1px solid var(--color-error)', borderRadius: 'var(--radius-md)', color: 'var(--color-error)', cursor: 'pointer', fontSize: 'var(--text-sm)' }}
                    >
                      <Trash2 size={14} /> {deletingId === org.id ? '...' : 'Desativar'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </AppLayout>
  );
}
